<template>
  <div class="user-menu-page">
    <QHeader class="bg-white text-primary header-ios-safe">
      <HeaderImageTitle :photo="etabProfile.getPhoto()">
        <QToolbar color="transparent">
          <QToolbarTitle v-if="etabProfile.getEtabName()">
            {{ displayTitle }}
          </QToolbarTitle>
        </QToolbar>
      </HeaderImageTitle>
    </QHeader>

    <QPageContainer>
      <QPage>
        <div>
          <UserMenu
            :is-master-etab="isMasterEtab"
            :is-master-etab-without-supervised-etab="isMasterEtabWithoutSupervisedEtab"
            @on-click-etab="onClickEtab"
            @on-click-informations="onClickInformations"
            @on-click-etab-admin-folder="onClickEtabAdminFolder"
            @on-click-subscription="OnClickSubscription"
            @on-click-help="onClickHelp"
            @on-click-logout="onClickLogout"
            @on-click-developer-tools="onClickDeveloperTools"
            @on-click-multi-account="onClickMultiAccount"
          />
        </div>
        <div class="user-menu-footer">
          <div>
            <QBtn flat :label="$t('ETAB.PAGES.DASHBOARD.USER_MENU.@CGU')" @click="onClickCGU" />
            <QBtn flat :label="$t('ETAB.PAGES.DASHBOARD.USER_MENU.@CONFIDENTIALITY')" @click="onClickConfidentiality" />
          </div>
          <div class="q-mt-sm">
            <AppVersion />
          </div>
        </div>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { useProfileStore } from "@etab/stores/profile";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ContactManager } from "@core/managers/contact.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";
  import { JavascriptHelpers } from "@core/helpers";
  import { GETTER_PROFILE_GET } from "@etab/stores/types";
  import { getActivePinia, mapState } from "pinia";
  import { ETAB_ROUTES } from "@etab/config";
  import { NEW_FAQ_ETAB_URL, ROLE, HELP_ACTIONS, INTERIM_EMAIL_EXTRACADABRA_SUPPORT } from "@core/constants";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";

  import AppVersion from "@core/components/utils/AppVersion.component.vue";
  import UserMenu from "@etab/components/dashboard/UserMenu.component.vue";
  import HeaderImageTitle from "@etab/components/dashboard/HeaderImageTitle.component.vue";

  export default {
    components: {
      UserMenu,
      HeaderImageTitle,
      AppVersion,
    },
    data() {
      return {
        hasSupervisedEtab: undefined,
        isMasterEtab: false,
      };
    },
    computed: {
      ...mapState(useProfileStore, {
        etabProfile: GETTER_PROFILE_GET,
      }),
      displayTitle() {
        return `${this.etabProfile.getEtabName()} - ${this.etabProfile.getCategoryLabel()}`;
      },
      isMasterEtabWithoutSupervisedEtab() {
        return this.isMasterEtab && !this.hasSupervisedEtab;
      },
    },
    mounted() {
      ScrollManager.scrollToTop();
      // we stop the loading started in beforeRoute
      this.$_stopGlobalLoading();
    },
    created() {
      this.initLink();
      this.initRole();
      this.hasCurrentSupervisedEtab();
      this.HELP_ACTIONS = HELP_ACTIONS;
    },
    methods: {
      async initRole() {
        const user = await UserStorageManager.getUser();
        if (user.role[0] === ROLE.MASTER) {
          this.isMasterEtab = true;
        }
      },
      async hasCurrentSupervisedEtab() {
        this.hasSupervisedEtab = !!(await UserStorageManager.getSupervisorData());
      },
      initLink() {
        this.NEW_FAQ_ETAB_URL = NEW_FAQ_ETAB_URL;
      },
      onClickEtab() {
        this.$router.push({ name: ETAB_ROUTES.account.profile.info.name }).catch((suppressedError) => {});
      },
      onClickPayment() {
        this.$router.push({ name: ETAB_ROUTES.payment.means.name }).catch((suppressedError) => {});
      },
      onClickEtabAdminFolder() {
        this.$router.push({ name: ETAB_ROUTES.account.adminFolderStatus.name }).catch((suppressedError) => {});
      },
      onClickInformations() {
        this.$router.push({ name: ETAB_ROUTES.account.accountInformations.name }).catch((suppressedError) => {});
      },
      OnClickSubscription() {
        this.$router.push({ name: ETAB_ROUTES.subscription.menu.name }).catch((suppressedError) => {});
      },
      onClickDeveloperTools() {
        this.$router.push({ name: ETAB_ROUTES.account.developer.name }).catch((suppressedError) => {});
      },
      async onClickLogout() {
        try {
          this.$_startGlobalLoading();
          // disconnect
          await AuthenticationManager.logout();
          getActivePinia()._s.forEach((store) => store.$reset());

          setTimeout(() => {
            // we reload the full app after logout in order to be sure we reset everything
            JavascriptHelpers.reloadWindow();
          }, 1000);
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        }
      },
      onClickConfidentiality() {
        this.$router.push({ name: ETAB_ROUTES.account.confidentiality.name }).catch((suppressedError) => {});
      },
      onClickCGU() {
        this.$router.push({ name: ETAB_ROUTES.account.cgu.name }).catch((suppressedError) => {});
      },
      onClickMultiAccount() {
        this.$router.push({ name: ETAB_ROUTES.account.multiAccount.name }).catch((suppressedError) => {});
      },
      onClickHelp() {
        const ACTIONS = {
          ASSISTANT: HELP_ACTIONS.ASSISTANT,
          SEND_EMAIL: HELP_ACTIONS.SEND_EMAIL,
          IMPROUVEMENT: HELP_ACTIONS.IMPROUVEMENT,
          OPEN_FAQ: HELP_ACTIONS.OPEN_FAQ,
        };
        this.$q
          .bottomSheet({
            message: this.$t("ETAB.PAGES.DASHBOARD.USER_MENU.@NEED_HELP"),
            actions: [
              {
                label: `${this.$t("ETAB.PAGES.DASHBOARD.USER_MENU.@WRITE_TO_US")}`,
                icon: "icon-f-mail",
                id: ACTIONS.SEND_EMAIL,
              },
              {
                label: `${this.$t("ETAB.PAGES.DASHBOARD.USER_MENU.@IMPROVEMENT")}`,
                icon: "icon-f-mail",
                id: ACTIONS.IMPROUVEMENT,
              },
              {
                label: this.$t("ETAB.PAGES.DASHBOARD.USER_MENU.@HELP"),
                icon: "icon-f-book-open",
                id: ACTIONS.OPEN_FAQ,
              },
              {
                label: this.$t("ETAB.PAGES.DASHBOARD.USER_MENU.@DISCOVER_ASSISTANT"),
                icon: "icon-f-bookmark",
                id: ACTIONS.ASSISTANT,
              },
            ],
          })
          .onOk((action) => {
            switch (action.id) {
              case ACTIONS.OPEN_FAQ: {
                TrackingManager.addEvent({
                  event: "profil_need_help_faq",
                });
                JavascriptHelpers.openUrl(NEW_FAQ_ETAB_URL, "_blank");
                break;
              }
              default:
              case ACTIONS.ASSISTANT: {
                TrackingManager.addEvent({
                  event: "profil_need_help_assistant",
                });
                this.$router.push({ name: ETAB_ROUTES.account.onboarding.name });
                break;
              }
              case ACTIONS.SEND_EMAIL: {
                TrackingManager.addEvent({
                  event: "profil_need_help_send_email",
                });
                if (this.profilEtab.isInterimEtab()) {
                  ContactManager.contactSupport({ email: INTERIM_EMAIL_EXTRACADABRA_SUPPORT });
                } else {
                  ContactManager.contactEtabSupport();
                }
                break;
              }
              case ACTIONS.IMPROUVEMENT: {
                TrackingManager.addEvent({
                  event: "profil_need_help_improve_app",
                });
                ContactManager.contactEtabSupport({ subject: "EMAILS.@ETAB_IMPROVE_APP" });
                break;
              }
            }
          });
      },
    },
  };
</script>

<style lang="scss">
  .user-menu-page {
    .q-page {
      padding: 15px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    h4 {
      color: $primary-lighter;
    }

    .user-menu-component {
      margin-top: 15px;
    }
    .user-menu-footer {
      display: flex;
      justify-content: space-between;
      @media screen and (max-width: $breakpoint-sm) {
        margin-bottom: 15px;
        text-align: center;
        flex-direction: column;
        > div {
          padding: 10px;
        }
      }
    }
  }
</style>
